import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Building2,
  Crown,
  Settings,
  CalendarDays,
  Inbox,
  Ticket,
  ArrowRight,
  Loader2,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-slate-400">{label}</p>
          <p className="font-heading text-3xl font-bold text-slate-900 mt-2">{value}</p>
          {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

const QUICK = [
  { to: "/agenda", label: "Agenda", desc: "Vista del día", icon: CalendarDays },
  { to: "/reservas", label: "Reservas", desc: "Todos los canales", icon: Ticket },
  { to: "/bandeja", label: "Bandeja a revisar", desc: "Confirmar reservas", icon: Inbox },
];

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/tours", { params: { activo: true } })
      .then(({ data }) => setTours(data))
      .finally(() => setLoading(false));
  }, []);

  const total = tours.length;
  const fuera = tours.filter((t) => t.categoria === "fuera_ciudad").length;
  const dentro = tours.filter((t) => t.categoria === "dentro_ciudad").length;
  // Los privados hoy son una MODALIDAD dentro del tour (no una categoría aparte), así
  // que se cuentan los tours que tienen esa modalidad configurada -- si no, siempre
  // quedaba en 0 aunque haya tours operando en privado.
  const toursPrivados = tours.filter(
    (t) => t.categoria === "privado" || (t.modalidades || []).some((m) => m.tipo === "privado")
  );
  const privados = toursPrivados.length;
  const privadosHint = privados > 0
    ? toursPrivados.slice(0, 3).map((t) => t.nombre).join(", ") + (privados > 3 ? ` +${privados - 3} más` : "")
    : "Ningún tour tiene modalidad privada aún";

  const hoy = new Date().toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="text-sm text-slate-500 capitalize">{hoy}</p>
        <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mt-1">
          Hola, {user?.nombre?.split(" ")[0]} 👋
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Este es el panel de operaciones de Tours NY. Entrá a la <span className="font-medium text-slate-700">Agenda</span> para ver
          la ocupación del día en tiempo real.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={MapPin} label="Tours activos" value={total} hint="Catálogo configurado" />
          <StatCard icon={Building2} label="Fuera de la ciudad" value={fuera} />
          <StatCard icon={Building2} label="Dentro de la ciudad" value={dentro} />
          <StatCard icon={Crown} label="Privados" value={privados} hint={privadosHint} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {QUICK.map((q) => {
          const Icon = q.icon;
          return (
            <button
              key={q.to}
              onClick={() => navigate(q.to)}
              data-testid={`quick-${q.to.replace("/", "")}`}
              className="group text-left"
            >
              <Card className="p-5 hover:border-slate-300 transition-colors h-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{q.label}</p>
                      <p className="text-xs text-slate-500">{q.desc}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-slate-600 transition-colors" />
                </div>
              </Card>
            </button>
          );
        })}
      </div>

      {isAdmin && (
        <Card className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700">
              <Settings className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Configuración del sistema</p>
              <p className="text-sm text-slate-500">Gestioná el catálogo de tours, usuarios y reglas de negocio.</p>
            </div>
          </div>
          <Button onClick={() => navigate("/configuracion")} data-testid="dashboard-go-config" className="gap-2">
            Ir a Configuración <ArrowRight className="h-4 w-4" />
          </Button>
        </Card>
      )}
    </div>
  );
}
